import { useMemo, useState } from 'react'
import { useNavigate, useParams } from 'react-router'
import { useCreateEntry, useEntry, useMoods, useUpdateEntry } from '@/features/entries/queries'
import type { EntryDraft } from '@/features/entries/types'
import { useAutosave } from '@/features/entries/use-autosave'
import { useMessages } from '@/features/language/use-language'
import type { Messages } from '@/features/language/messages'
import { isApiError } from '@/shared/api/problem'
import { Chip } from '@/shared/ui/Chip'
import { StatusMessage } from '@/shared/ui/StatusMessage'

/**
 * 표시가 답하는 것은 하나다. 지금 화면에 있는 글이 서버에 갔는가.
 *
 * 요청이 언제 나가는지는 알리지 않는다. 그래서 저장 리듬을 늦춰도 표시가 늦어지지 않고 글자를
 * 친 순간 반응이 온다. 아직 아무것도 쓰지 않았으면 자리를 비워 둔다.
 *
 * 실패한 자리에 안심 문장을 덧붙이지 않는다. 쓴 글이 화면에 그대로 있으므로 사라지지 않았다는
 * 것이 문구 없이도 드러난다.
 */
function autosaveMessage(m: Messages) {
  return {
    idle: '',
    pending: m.entry.editor.saving,
    saving: m.entry.editor.saving,
    saved: m.entry.editor.saved,
    error: m.entry.editor.saveFailed,
  }
}

/** 입력이 멎고 이만큼 지나면 저장한다. */
const AUTOSAVE_DELAY_MS = 5_000
/** 쉬지 않고 써도 마지막 저장에서 이만큼 지나면 한 번 보낸다. 잃을 수 있는 최대치가 이 값이다. */
const AUTOSAVE_MAX_WAIT_MS = 15_000

export function EntryEditorPage() {
  const m = useMessages()
  const { entryId } = useParams<{ entryId: string }>()
  const entryQuery = useEntry(entryId)

  if (!entryId) {
    return <EntryEditor initialDraft={{ content: '', moodCode: null }} />
  }
  if (entryQuery.isPending) {
    return <StatusMessage>{m.entry.editor.opening}</StatusMessage>
  }
  if (entryQuery.isError || !entryQuery.data) {
    return (
      <StatusMessage
        action={{ label: m.app.common.retry, onClick: () => void entryQuery.refetch() }}
      >
        {m.entry.editor.openFailed}
      </StatusMessage>
    )
  }

  // 불러온 값을 초기 상태로 삼는다. 다른 기록으로 옮기면 key가 바뀌어 편집기를 새로 세운다.
  return (
    <EntryEditor
      key={entryId}
      entryId={entryId}
      initialDraft={{ content: entryQuery.data.content, moodCode: entryQuery.data.moodCode }}
    />
  )
}

interface EntryEditorProps {
  entryId?: string
  initialDraft: EntryDraft
}

function EntryEditor({ entryId, initialDraft }: EntryEditorProps) {
  const m = useMessages()
  const navigate = useNavigate()
  const moodsQuery = useMoods()
  const createEntry = useCreateEntry()
  const updateEntry = useUpdateEntry(entryId ?? '')

  const [content, setContent] = useState(initialDraft.content)
  const [moodCode, setMoodCode] = useState<string | null>(initialDraft.moodCode)

  const draft = useMemo<EntryDraft>(() => ({ content, moodCode }), [content, moodCode])

  const { status, flush } = useAutosave({
    value: draft,
    delayMs: AUTOSAVE_DELAY_MS,
    maxWaitMs: AUTOSAVE_MAX_WAIT_MS,
    enabled: content.trim().length > 0 || moodCode !== null,
    onSave: async (value) => {
      try {
        if (entryId) {
          await updateEntry.mutateAsync(value)
          return
        }
        const created = await createEntry.mutateAsync(value)
        // 새 기록은 첫 저장에서 식별자를 받는다. 이후 갱신이 같은 기록으로 가도록 주소를 바꾼다.
        await navigate(`/entries/${created.id}`, { replace: true })
      } catch (error) {
        /*
         * Session이 끊겨 거절된 것은 저장 실패로 두지 않는다. 다시 보내도 계속 거절이라 실패
         * 표시만 남고 그동안 사용자는 저장되는 줄 알고 계속 쓴다.
         */
        if (isApiError(error) && error.isUnauthorized) {
          await navigate('/login', { replace: true, state: { sessionExpired: true } })
        }
        throw error
      }
    },
  })

  return (
    <div className="flex flex-col gap-5">
      <div>
        <p className="text-ink-muted text-sm">{m.entry.editor.moodHint}</p>
        <div className="mt-3 flex gap-2 overflow-x-auto pb-1">
          {moodsQuery.data?.map((mood) => (
            <Chip
              key={mood.code}
              selected={moodCode === mood.code}
              onClick={() => setMoodCode(moodCode === mood.code ? null : mood.code)}
            >
              <span aria-hidden="true">{mood.emoji}</span>
              {moodLabel(m, mood.code, mood.label)}
            </Chip>
          ))}
        </div>
      </div>

      <textarea
        value={content}
        onChange={(event) => setContent(event.target.value)}
        autoFocus
        rows={12}
        placeholder={m.entry.editor.placeholder}
        aria-label={m.entry.editor.bodyLabel}
        className="bg-surface border-border/70 rounded-card min-h-72 resize-none border px-5 py-4 leading-loose outline-none"
      />

      <p aria-live="polite" className="text-ink-muted min-h-5 text-sm">
        {status === 'error' ? (
          // 실패한 자리를 눌러 다시 보낸다. 다시 시도할 곳을 따로 만들지 않는다.
          <button
            type="button"
            onClick={() => void flush()}
            className="underline underline-offset-4"
          >
            {autosaveMessage(m).error}
          </button>
        ) : (
          autosaveMessage(m)[status]
        )}
      </p>
    </div>
  )
}

/**
 * 무드 라벨은 화면 언어를 따른다. 값의 정체는 코드와 이모지가 지키고 라벨은 그 값을 지금 화면의
 * 말로 부르는 표기다. 사전이 모르는 코드가 오면 서버가 준 라벨을 그대로 세운다. 화면에 빈자리를
 * 두지 않는다.
 */
function moodLabel(m: Messages, code: string, fallback: string): string {
  const labels: Record<string, string> = m.entry.mood
  return labels[code] ?? fallback
}
